import React from 'react';
import { animated, useSpring } from 'react-spring';
import styled, { css } from 'styled-components';
import Checkbox, {
  Props as CheckboxProps,
} from '~/components/molecule/Checkbox';
import JustificationContainer from '~/components/atom/JustificationContainer';

export type Props = {
  selectable: boolean;
  label: React.ReactNode;
  /** Callback when the label is clicked */
  onLabelClick?: () => void;
  /** Prevents the label area from triggering onChange */
  disableOnChangeViaLabel?: boolean;
} & Omit<CheckboxProps, 'label'>;

const AnimatedCheckbox: React.FCC<Props> = ({
  selectable,
  label,
  /** Prevents the label area from triggering onChange */
  disableOnChangeViaLabel,
  /** Callback when the label is clicked */
  onLabelClick,
  onChange,
  ...rest
}) => {
  const checkboxStyle = useSpring({
    to: {
      opacity: selectable ? 1 : 0,
      transform: `translateX(${selectable ? '0px' : '-20px'})`,
    },
  });

  const labelStyle = useSpring({
    to: {
      transform: `translateX(${selectable ? '0px' : '-30px'})`,
    },
    config: {
      tension: 180,
      friction: 20,
    },
  });

  return (
    <Container
      justification="start"
      align="center"
      nonResponsive
      selectable={selectable}
    >
      <animated.div style={checkboxStyle}>
        <StyledCheckbox onChange={onChange} {...rest} />
      </animated.div>
      <animated.div
        style={labelStyle}
        onClick={e => {
          e.stopPropagation();

          if (onLabelClick) {
            onLabelClick();
            return;
          }

          if (disableOnChangeViaLabel) return;
          onChange?.(e);
        }}
      >
        {label}
      </animated.div>
    </Container>
  );
};

const StyledCheckbox = styled(Checkbox)<{}>`
  width: auto;
`;

const Container = styled(JustificationContainer)<{ selectable: boolean }>(
  ({ selectable }) => css`
    cursor: ${selectable ? 'pointer' : 'default'};
  `,
);
export default AnimatedCheckbox;
